import { useState, useRef, useEffect, useMemo } from 'react'
import { Box, Typography, Paper, Avatar, TextField, IconButton, AvatarGroup, Stack } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { SIZING } from '@common/theme/sizing'
import { SPACING } from '@common/theme/spacing'
import { StudyQueryDetailDataSchema, StudyQueryMessageSchema } from '@common/config/api/client'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'

export interface QueryMessagesPanelProps {
  query: StudyQueryDetailDataSchema
  onSendMessage: (queryId: string, message: string) => void
  currentUserId?: string
  isReadOnly?: boolean
}

export default function QueryMessagesPanel({
  query,
  onSendMessage,
  isReadOnly = false,
}: Readonly<QueryMessagesPanelProps>) {
  const [newMessage, setNewMessage] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const auth = useExtendedAuth()

  const currentUserId = useMemo(() => auth.user?.profile.sub, [auth.user])

  useEffect(() => {
    scrollToBottom()
  }, [query?.messages])

  const handleSendMessage = () => {
    if (newMessage.trim() && !isReadOnly) {
      onSendMessage(query.id, newMessage)
      setNewMessage('')
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey && !isReadOnly) {
      event.preventDefault()
      handleSendMessage()
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const uniqueAssignees =
    query?.assignees?.filter((assignee, index, self) => index === self.findIndex((a) => a.id === assignee.id)) || []

  const generateHeaderText = () => {
    if (uniqueAssignees.length === 0) return ''
    if (uniqueAssignees.length === 1) return uniqueAssignees[0].name

    const names = uniqueAssignees.map((a) => a.name)
    if (names.length === 2) return names.join(', ')
    if (names.length === 3) return `${names[0]}, ${names[1]}, ${names[2]}`

    return `${names[0]}, ${names[1]}, ${names[2]},...`
  }

  const isSystemNotification = (message: StudyQueryMessageSchema) => message.title === 'System notification'

  const shouldShowSender = (message: StudyQueryMessageSchema, index: number) => {
    if (isSystemNotification(message)) return false
    const previous = query.messages?.at(index - 1)
    if (previous && isSystemNotification(previous)) return true
    return index === 0 || previous?.sender.id !== message.sender.id
  }

  const renderSystemNotification = (message: StudyQueryMessageSchema) => (
    <Stack key={message.id} alignItems="center" spacing={0.5} sx={{ mt: 2, width: '100%' }}>
      <Typography variant="subtitle2" color="text.secondary">
        {message.title}
      </Typography>
      <Typography variant="body2">{message.message}</Typography>
      <Typography variant="caption" color="text.secondary">
        {new Date(message.sent_at).toLocaleString()}
      </Typography>
    </Stack>
  )

  const renderMessageGroup = (message: StudyQueryMessageSchema, index: number) => {
    if (isSystemNotification(message)) {
      return renderSystemNotification(message)
    }

    const isCurrentUser = message.sender.id === currentUserId
    const showSender = shouldShowSender(message, index)
    const senderInitials = message.sender?.name
      ? message.sender.name
          .split(' ')
          .map((n) => n[0])
          .join('')
      : '?'
    return (
      <Stack
        key={message.id}
        alignItems={isCurrentUser ? 'flex-end' : 'flex-start'}
        spacing={0.5}
        sx={{
          mt: showSender ? 2 : 0.5,
        }}
      >
        {showSender && !isCurrentUser && (
          <Stack direction="row" alignItems="center" spacing={SPACING.minSpacing}>
            <Avatar
              sx={{
                width: SIZING.chatAvatarSize,
                height: SIZING.chatAvatarSize,
                fontSize: SIZING.chatAvatarFontSize,
                bgcolor: 'secondary.main',
                color: 'text.primary',
                mr: 1,
              }}
            >
              {senderInitials}
            </Avatar>
            <Typography variant="subtitle2">{message.sender.name}</Typography>
          </Stack>
        )}

        <Paper
          elevation={0}
          sx={{
            px: SPACING.spacingXl,
            py: SPACING.spacingLg,
            maxWidth: '70%',
            marginLeft: !isCurrentUser ? '40px !important' : '',
            borderRadius: SPACING.borderRadiusXl,
            bgcolor: isCurrentUser ? 'success-alt.light' : 'info-alt.light',
            border: '1px solid',
            borderColor: isCurrentUser ? 'success-alt.main' : 'info-alt.main',
            alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
          }}
        >
          {message.title && (
            <Typography variant="subtitle2" sx={{ mb: SPACING.minSpacing }}>
              {message.title}
            </Typography>
          )}
          <Typography variant="body2">{message.message}</Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              textAlign: 'right',
              mt: SPACING.spacingSm,
            }}
          >
            {new Date(message.sent_at).toLocaleString()}
          </Typography>
        </Paper>
      </Stack>
    )
  }

  return (
    <Stack
      direction="column"
      sx={{
        height: '100%',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={SPACING.spacingLg}
        sx={{
          px: SPACING.spacingXxl,
          py: SPACING.minSpacing,
          borderBottom: '1px solid',
          borderColor: 'secondary.main',
        }}
      >
        {uniqueAssignees.length > 1 ? (
          <AvatarGroup
            max={3}
            slotProps={{
              surplus: {
                sx: {
                  width: SIZING.chatAvatarSize,
                  height: SIZING.chatAvatarSize,
                  fontSize: SIZING.chatAvatarFontSize,
                  color: 'text.primary',
                  bgcolor: 'secondary.main',
                  border: 'none',
                },
              },
            }}
          >
            {uniqueAssignees.map((assignee) => (
              <Avatar
                key={assignee.id}
                sx={{
                  width: SIZING.chatAvatarSize,
                  height: SIZING.chatAvatarSize,
                  fontSize: SIZING.chatAvatarFontSize,
                  color: 'text.primary',
                  bgcolor: 'secondary.main',
                }}
              >
                {assignee?.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')}
              </Avatar>
            ))}
          </AvatarGroup>
        ) : (
          uniqueAssignees.length === 1 && (
            <Avatar
              sx={{
                width: SIZING.chatAvatarSize,
                height: SIZING.chatAvatarSize,
                fontSize: SIZING.chatAvatarFontSize,
                color: 'text.primary',
                bgcolor: 'secondary.main',
              }}
            >
              {uniqueAssignees[0].name
                .split(' ')
                .map((n) => n[0])
                .join('')}
            </Avatar>
          )
        )}
        <Box>
          <Typography variant="subtitle2" color="text.primary">
            {generateHeaderText()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Site representatives
          </Typography>
        </Box>
      </Stack>

      <Stack
        spacing={SPACING.spacingXl}
        flex={1}
        sx={{
          overflowY: 'auto',
          px: SPACING.spacingXxl,
          py: SPACING.spacingXl,
        }}
      >
        {query?.messages?.map((message, index) => renderMessageGroup(message, index))}
        <div ref={messagesEndRef} />
      </Stack>

      {!isReadOnly ? (
        <Box
          sx={{
            py: SPACING.spacingXl,
            px: SPACING.spacingXxl,
            borderTop: '1px solid',
            borderColor: 'secondary.main',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid',
              borderColor: 'secondary.main',
              borderRadius: SPACING.borderRadiusSm,
              px: SPACING.chatInputInnerPaddingX,
              py: SPACING.chatInputInnerPaddingY,
            }}
          >
            <TextField
              fullWidth
              placeholder="Message"
              variant="standard"
              InputProps={{
                disableUnderline: true,
              }}
              size="small"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <IconButton
              onClick={handleSendMessage}
              disabled={!newMessage.trim()}
              color="primary"
              size="small"
              sx={{ ml: SPACING.minSpacing }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            py: SPACING.spacingXl,
            px: SPACING.spacingXxl,
            borderTop: '1px solid',
            borderColor: 'secondary.main',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'secondary.light',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            You are in view-only mode and cannot send messages
          </Typography>
        </Box>
      )}
    </Stack>
  )
}
